/* =====================================================================
 * bc-page-vitals.js — Beyond Carat per-pageview engagement vitals beacon
 *
 * Moved out of snippets/bc-page-vitals.liquid on 2026-09-05 (perf item 27).
 * It was ~8 KB of inline, uncacheable script on every navigation; the snippet
 * had no per-request Liquid in it at all, so nothing was gained by inlining
 * it. As an `asset_url` file it is fetched once per deploy and served from the
 * CDN with a content hash.
 *
 * ORDERING: rendered `defer` in the same end-of-<body> position the inline
 * block held. It already waited for DOMContentLoaded before attaching
 * listeners, so `defer` changes nothing about when it starts; it reads
 * window.__bcCfg / window.__bcAid, which the datalayer-init inline core still
 * sets first in <head>.
 *
 * WHAT IT DOES
 * ------------
 * Emits ONE `page_vitals` signal per pageview to the tracking front door at
 * https://events.beyondcarat.com/collect. The signal is a small bag of
 * AGGREGATE engagement metrics — foreground time, scroll depth, interaction
 * count, a rage-click flag. It carries NO coordinates, NO key contents and NO
 * element text: only counts and coarse percentages that describe how engaged
 * the visit was, never what was typed or where the pointer went.
 *
 *   engagement_ms                — GA4-style foreground-visible time.
 *   max_scroll_pct               — deepest scroll reached, 0-100. THE scroll
 *                                  number bc-tracking reads: src/shared/
 *                                  crm-forward.ts derives the CRM's
 *                                  scroll_depth from it (perf item 23).
 *   interaction_count            — pointerdown + keydown + touchstart total.
 *   time_to_first_interaction_ms — ms to first interaction (null if none).
 *   tab_hidden_count             — visible -> hidden transitions.
 *   rage_click                   — 3+ pointerdowns within 700ms in a ~30px area.
 *   is_touch                     — coarse pointer / touch capability.
 *
 * Firing rule (Safari-verified "first-hidden snapshot" semantics): send ONCE
 * per pageview, on the FIRST visibilitychange->hidden OR on pagehide,
 * whichever comes first, guarded by a flag. Deliberately does NOT re-send when
 * the tab later returns to the foreground.
 *
 * TRANSPORT / PRIVACY: navigator.sendBeacon with a text/plain Blob is primary
 * (survives unload without a preflight); keepalive fetch is the fallback. The
 * bag rides the SAME /collect envelope every other storefront event uses, so
 * auth travels as the top-level `write_key` body field and identity resolves
 * from the bc_aid cookie the request carries. Respects window.bcConsent
 * (explicit "no" only; see trackingAllowed). Passive listeners only.
 * ===================================================================== */
(function () {
  'use strict';

  var ENDPOINT = 'https://events.beyondcarat.com/collect';
  var SESSION_GAP_MS = 30 * 60 * 1000;   // 30 min idle = new session (matches bc-fingerprint)
  var RAGE_WINDOW_MS = 700;              // 3+ clicks inside this window …
  var RAGE_RADIUS_PX = 30;               // … within this pixel radius = rage click
  var RAGE_MIN_CLICKS = 3;

  function getCookie(name) {
    var m = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return m ? decodeURIComponent(m[2]) : null;
  }

  // Reuse the established sessionStorage keys so the session definition stays
  // identical to bc-fingerprint / bc-presence.
  function getOrCreateSessionId() {
    // ONE session rule (round 2). `bc-tracking-async.js` publishes its own
    // `currentSessionId` as `window.__bcSessionId`; it is a `defer` script in
    // <head> and this one is `defer` at the end of <body>, so it has run. The
    // local copy below stays as the fallback for the ways that publish can be
    // missing ENTIRELY: the tracker asset 404s, the CSP refuses it, or it
    // throws before the assignment. NOT because it is "bot-gated" — corrected
    // round 3, 2026-09-06: the file's only bot gate returns from a nested IIFE
    // (bc-tracking-async.js ~:3908, which says so at the call site) and nothing
    // above the publish (~:187) can return at top level. A missing session id
    // must never be an exception, whatever the cause. Reading either one
    // REFRESHES `bc_sess_ts`, which is the same behaviour this copy always had.
    try {
      if (typeof window.__bcSessionId === 'function') return window.__bcSessionId();
    } catch (e) {}
    var KEY = 'bc_sess';
    var TS_KEY = 'bc_sess_ts';
    var now = Date.now();
    try {
      var id = sessionStorage.getItem(KEY);
      var lastTs = parseInt(sessionStorage.getItem(TS_KEY) || '0', 10);
      if (!id || (lastTs && (now - lastTs) > SESSION_GAP_MS)) {
        id = (window.crypto && crypto.randomUUID) ? crypto.randomUUID() : (
          'sess-' + now.toString(36) + '-' + Math.random().toString(36).slice(2, 10)
        );
        sessionStorage.setItem(KEY, id);
      }
      sessionStorage.setItem(TS_KEY, String(now));
      return id;
    } catch (e) {
      return 'nosess-' + now.toString(36) + '-' + Math.random().toString(36).slice(2, 10);
    }
  }

  // CONSENT (2026-09-29, lane A): the ONE helper, window.bcConsent
  // (snippets/consent-mode-default.liquid) — explicit analytics/marketing "no"
  // stops the beacon, unknown is allowed, and the envelope carries the real
  // answer instead of `{state: null}`. No wait here: this beacon is sent at
  // page exit, long after the privacy API has loaded, and an unload cannot
  // be deferred.
  function trackingAllowed() {
    try {
      if (window.bcConsent) return window.bcConsent.captureAllowed();
      var c = window.Shopify && Shopify.customerPrivacy;
      if (c && typeof c.currentVisitorConsent === 'function') {
        var v = c.currentVisitorConsent() || {};
        return v.marketing !== 'no' && v.analytics !== 'no';
      }
    } catch (e) {}
    return true;
  }
  function consentStamp() {
    try { if (window.bcConsent) return window.bcConsent.stamp(); } catch (e) {}
    return { state: null };
  }

  // Same /collect envelope shape as bc-tracking-async.js buildCollectEnvelope.
  // Replicated (not imported) because that builder is private to the async
  // asset's IIFE; the door reads `write_key` from the body, and identity from
  // the bc_aid cookie this request carries — the body bc_aid is documentary.
  function buildEnvelope(payload) {
    var aid = (window.__bcAid) || getCookie('bc_aid') || null;
    var cfg = window.__bcCfg || {};
    return {
      v: 1,
      // Payload contract v2 (see CAPTURE_V in assets/bc-tracking-async.js):
      // `event` and `ua` are gone — the door reads `event_name`, and the
      // envelope's ua comes from the request header, never the body.
      capture_v: 2,
      source: 'storefront',
      write_key: cfg.writeKey || null,
      event_name: 'page_vitals',
      bc_aid: aid,
      bc_aid_candidate: aid ? null : (window.__bcAidCandidate || null),
      consent: consentStamp(),
      url: (window.__bcLandingUrl ? window.__bcLandingUrl() : location.href),
      referrer: document.referrer || '',
      payload: payload
    };
  }

  // sendBeacon(text/plain Blob) primary, keepalive fetch fallback — the exact
  // transport the presence + checkout-intent beacons use so it survives unload.
  function sendDoorBeacon(envelope) {
    try {
      var body = JSON.stringify(envelope);
      try {
        if (navigator.sendBeacon) {
          var blob = new Blob([body], { type: 'text/plain' });
          if (navigator.sendBeacon(ENDPOINT, blob)) return;
        }
      } catch (e) { /* fall through to fetch */ }
      fetch(ENDPOINT, {
        method: 'POST',
        body: body,
        headers: { 'Content-Type': 'text/plain' },
        keepalive: true,
        mode: 'cors',
        credentials: 'include'
      }).catch(function () { /* silent — vitals are best-effort */ });
    } catch (e) { /* silent */ }
  }

  function init() {
    var startTs = Date.now();

    // ---- Foreground-time accumulator (GA4 engagement_ms semantics) ----------
    // Sum wall-clock time spent with the tab visible. `visibleStart` marks the
    // open slice; accumulate() closes it into engagementMs and clears the mark.
    var engagementMs = 0;
    var visibleStart = (document.visibilityState === 'visible') ? startTs : 0;
    function accumulate() {
      if (visibleStart) {
        engagementMs += Date.now() - visibleStart;
        visibleStart = 0;
      }
    }

    // ---- Scroll depth (rAF-throttled, passive) ------------------------------
    var maxScrollPct = 0;
    var scrollQueued = false;
    function measureScroll() {
      scrollQueued = false;
      try {
        var doc = document.documentElement;
        var total = doc.scrollHeight;
        if (total > 0) {
          var seen = (window.scrollY || doc.scrollTop || 0) + window.innerHeight;
          var pct = Math.round((seen / total) * 100);
          if (pct < 0) pct = 0; if (pct > 100) pct = 100;
          if (pct > maxScrollPct) maxScrollPct = pct;
        }
      } catch (e) {}
    }
    function onScroll() {
      if (scrollQueued) return;
      scrollQueued = true;
      if ('requestAnimationFrame' in window) requestAnimationFrame(measureScroll);
      else measureScroll();
    }

    // ---- Interaction counters + first-interaction timing --------------------
    var interactionCount = 0;
    var firstInteractionMs = null;
    function noteInteraction() {
      interactionCount++;
      if (firstInteractionMs === null) firstInteractionMs = Date.now() - startTs;
    }

    // ---- Rage-click detection (no coordinates leave this scope) --------------
    // Transient buffer of the last few pointerdowns as rounded {t,x,y}; once a
    // burst is detected the flag latches and the buffer is irrelevant. Nothing
    // here is ever placed in the payload — only the boolean result is.
    var rageClick = false;
    var recentClicks = [];
    function notePointer(x, y) {
      if (rageClick) return;
      var now = Date.now();
      recentClicks.push({ t: now, x: Math.round(x || 0), y: Math.round(y || 0) });
      // Keep only clicks inside the rolling window.
      while (recentClicks.length && (now - recentClicks[0].t) > RAGE_WINDOW_MS) {
        recentClicks.shift();
      }
      if (recentClicks.length >= RAGE_MIN_CLICKS) {
        var last = recentClicks[recentClicks.length - 1];
        var tight = true;
        for (var i = 0; i < recentClicks.length; i++) {
          if (Math.abs(recentClicks[i].x - last.x) > RAGE_RADIUS_PX ||
              Math.abs(recentClicks[i].y - last.y) > RAGE_RADIUS_PX) { tight = false; break; }
        }
        if (tight) { rageClick = true; recentClicks.length = 0; }
      }
    }

    var isTouch = false;
    try { isTouch = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0); } catch (e) {}

    var tabHiddenCount = 0;

    // ---- One-shot snapshot + send ------------------------------------------
    var sent = false;
    function sendVitals() {
      if (sent) return;
      sent = true;
      if (!trackingAllowed()) return;
      accumulate(); // close the current visible slice before snapshotting
      var payload = {
        event_name: 'page_vitals',
        bc_aid: (window.__bcAid) || getCookie('bc_aid') || null,
        session_id: getOrCreateSessionId(),
        path: location.pathname,
        engagement_ms: engagementMs,
        max_scroll_pct: maxScrollPct,
        interaction_count: interactionCount,
        time_to_first_interaction_ms: firstInteractionMs,
        tab_hidden_count: tabHiddenCount,
        rage_click: rageClick,
        is_touch: isTouch
      };
      sendDoorBeacon(buildEnvelope(payload));
    }

    // ---- Listeners (all passive) --------------------------------------------
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('pointerdown', function (e) {
      noteInteraction();
      notePointer(e.clientX, e.clientY);
    }, { passive: true });
    window.addEventListener('keydown', noteInteraction, { passive: true });
    window.addEventListener('touchstart', noteInteraction, { passive: true });

    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') {
        tabHiddenCount++;
        accumulate();
        sendVitals();            // first hidden wins
      } else {
        if (!visibleStart) visibleStart = Date.now();
      }
    });
    // pagehide covers a hard nav / unload that never fired a hidden transition.
    window.addEventListener('pagehide', sendVitals);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
